import React, { useState } from "react";
import { Button, Modal, App } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import GraduationCapIcon from "../GraduationCapIcon";
import { TrainingRow } from "../../../../services/formacion/formacion.types";
import { updateTraining } from "../../../../services/formacion/formacion.service";
import { getApiErrorMessage } from "../../../../utils";
import CoverUploadInput from "../TrainingModal/CoverUploadInput";
import styles from "../TrainingModal/styles.module.scss";

interface Props {
  open: boolean;
  schoolId: number | null;
  training: TrainingRow;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * SCRUM-1007: a completed training can no longer be edited as a whole, but its
 * cover still can. The backend allows a cover-only PATCH on completed
 * trainings (`TrainingsService.isCoverOnlyUpdate`), so this modal sends
 * `cover_image_url` and nothing else — every other field, and the recording
 * link, stay untouched.
 */
const CoverModal: React.FC<Props> = ({
  open,
  schoolId,
  training,
  onClose,
  onSaved,
}) => {
  const { notification } = App.useApp();
  const [coverUrl, setCoverUrl] = useState<string | null>(
    training.cover_image_url
  );
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (coverUrl === training.cover_image_url) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      await updateTraining(schoolId, training.id, {
        cover_image_url: coverUrl,
      });
      notification.success({
        message: "Éxito",
        description: "La portada fue actualizada exitosamente!",
        placement: "top",
        duration: 3,
      });
      onSaved();
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: getApiErrorMessage(
          error,
          "La portada no pudo ser actualizada."
        ),
        placement: "top",
        duration: 4,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      closeIcon={<CloseOutlined className={styles.closeIcon} />}
      title={
        <div className={styles.header}>
          <GraduationCapIcon className={styles.headerIcon} />
          <span>Editar portada</span>
        </div>
      }
    >
      <div style={{ marginBottom: 8, fontWeight: 500 }}>{training.title}</div>
      <div style={{ marginBottom: 16 }}>
        <CoverUploadInput
          schoolId={schoolId}
          trainingId={training.id}
          value={coverUrl}
          onChange={setCoverUrl}
        />
      </div>

      <div className={styles.footer}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          type="primary"
          className={styles.ctaButton}
          loading={submitting}
          onClick={handleSave}
        >
          Guardar
        </Button>
      </div>
    </Modal>
  );
};

export default CoverModal;
